import { useMemo, useState } from 'react';
import {
  ChevronRight,
  Columns3,
  Database,
  Eye,
  KeyRound,
  Lock,
  Play,
  RefreshCw,
  Search,
  Table2,
} from 'lucide-react';
import clsx from 'clsx';
import type { CatalogColumn, CatalogSnapshot, CatalogTable } from '@idas/shared';

interface Props {
  snapshot: CatalogSnapshot | undefined;
  loading: boolean;
  refreshing: boolean;
  onRefresh: () => void;
  /** Inserts text at the cursor — how clicking a table gets it into the query. */
  onInsert: (text: string) => void;
  /** Opens a new tab with `SELECT * FROM table` and runs it immediately. */
  onQuickRun: (database: string, table: string) => void;
  /**
   * The database unqualified names resolve against, or null if none is chosen.
   * Tables inside it are inserted bare; anything else keeps its prefix.
   */
  activeDatabase: string | null;
  onSelectDatabase: (database: string) => void;
}

/**
 * The schema browser.
 *
 * Restricted objects are shown rather than hidden, with the reason legible.
 * Hiding them would leave analysts writing queries against tables that
 * silently do not exist for them and filing tickets about it; showing them
 * greyed out communicates the boundary directly. (Denied tables arrive from
 * the API with an empty column list, so nothing about their contents leaks.)
 */
export function SchemaTree({
  snapshot,
  loading,
  refreshing,
  onRefresh,
  onInsert,
  onQuickRun,
  activeDatabase,
  onSelectDatabase,
}: Props) {
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const filtered = useMemo(() => {
    if (!snapshot) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return snapshot.databases;

    return snapshot.databases
      .map((database) => ({
        ...database,
        tables: database.tables.filter(
          (table) =>
            table.name.toLowerCase().includes(needle) ||
            // Searching by column is how you find the table you half-remember.
            table.columns.some((column) => column.name.toLowerCase().includes(needle)),
        ),
      }))
      .filter((database) => database.tables.length > 0);
  }, [snapshot, search]);

  // While searching, auto-expand: a filtered tree that is still collapsed
  // shows the user nothing but the count of what they were looking for.
  const searching = search.trim().length > 0;

  const toggle = (key: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const totalTables = snapshot?.databases.reduce((sum, d) => sum + d.tables.length, 0) ?? 0;

  return (
    <div className="flex h-full flex-col bg-surface-raised">
      <div className="flex items-center gap-1 border-b border-edge px-2 py-1.5">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" />
          <input
            className="input py-1 pl-7 text-2xs"
            placeholder="Search tables and columns"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <button
          className="btn btn-ghost px-1.5"
          onClick={onRefresh}
          disabled={refreshing}
          title="Re-read the schema from the database"
          aria-label="Refresh schema"
        >
          <RefreshCw className={clsx('h-3 w-3', refreshing && 'animate-spin')} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto py-1">
        {loading && <TreeSkeleton />}

        {!loading && filtered.length === 0 && (
          <p className="px-3 py-6 text-center text-2xs leading-relaxed text-ink-faint">
            {searching
              ? `Nothing matches “${search}”.`
              : 'No tables visible. Either the schema has not been read yet, or policy hides everything on this connection.'}
          </p>
        )}

        {filtered.map((database) => {
          const dbKey = database.name;
          const open = searching || expanded.has(dbKey);
          const active = dbKey === activeDatabase;

          return (
            <div key={dbKey}>
              {/* Two controls on one row: the chevron expands, the name
                  selects. Expanding a database to look at it is not the same
                  as pointing your query at it, and conflating them would make
                  browsing silently rewrite what `FROM user` means. */}
              <div
                className={clsx(
                  'flex w-full items-center gap-1 px-2 py-1',
                  active ? 'bg-accent/10' : 'hover:bg-surface-sunken',
                )}
              >
                <button
                  className="shrink-0"
                  onClick={() => toggle(dbKey)}
                  aria-label={open ? `Collapse ${dbKey}` : `Expand ${dbKey}`}
                >
                  <ChevronRight
                    className={clsx(
                      'h-3 w-3 text-ink-faint transition-transform',
                      open && 'rotate-90',
                    )}
                  />
                </button>

                <button
                  className="flex min-w-0 flex-1 items-center gap-1 text-left"
                  onClick={() => onSelectDatabase(dbKey)}
                  title={
                    active
                      ? `${dbKey} is active — unqualified table names resolve here`
                      : `Use ${dbKey} for unqualified table names`
                  }
                >
                  <Database
                    className={clsx('h-3 w-3 shrink-0', active ? 'text-accent' : 'text-ink-faint')}
                  />
                  <span
                    className={clsx(
                      'truncate text-2xs',
                      active ? 'font-semibold text-accent' : 'font-medium text-ink',
                    )}
                  >
                    {database.name}
                  </span>
                  {active && (
                    <span className="shrink-0 rounded-full bg-accent/20 px-1.5 text-2xs text-accent">
                      active
                    </span>
                  )}
                </button>

                <span className="ml-auto shrink-0 text-2xs text-ink-faint">
                  {database.tables.length}
                </span>
              </div>

              {open &&
                database.tables.map((table) => (
                  <TableNode
                    key={`${dbKey}.${table.name}`}
                    database={dbKey}
                    table={table}
                    qualify={!active}
                    expanded={searching || expanded.has(`${dbKey}.${table.name}`)}
                    onToggle={() => toggle(`${dbKey}.${table.name}`)}
                    onInsert={onInsert}
                    onQuickRun={onQuickRun}
                  />
                ))}
            </div>
          );
        })}
      </div>

      {snapshot && (
        <div className="border-t border-edge px-2 py-1 text-2xs text-ink-faint">
          {totalTables} objects · read {timeAgo(snapshot.refreshedAt)}
        </div>
      )}
    </div>
  );
}

function TableNode({
  database,
  table,
  qualify,
  expanded,
  onToggle,
  onInsert,
  onQuickRun,
}: {
  database: string;
  table: CatalogTable;
  /**
   * Whether this table needs its database prefix. False when its database is
   * the active one, so the console writes the same short form an analyst
   * would — `FROM user`, not `FROM intervalteam_test.user`.
   */
  qualify: boolean;
  expanded: boolean;
  onToggle: () => void;
  onInsert: (text: string) => void;
  onQuickRun: (database: string, table: string) => void;
}) {
  const Icon = table.kind === 'VIEW' ? Eye : Table2;
  const reference = qualify ? `${database}.${table.name}` : table.name;

  return (
    <div>
      <div
        className={clsx(
          'group flex items-center gap-1 py-0.5 pl-5 pr-2',
          table.denied ? 'opacity-50' : 'hover:bg-surface-sunken',
        )}
      >
        <button
          className="flex min-w-0 flex-1 items-center gap-1 text-left"
          onClick={table.denied ? undefined : onToggle}
          disabled={table.denied}
        >
          <ChevronRight
            className={clsx(
              'h-3 w-3 shrink-0 text-ink-faint transition-transform',
              expanded && 'rotate-90',
              table.denied && 'invisible',
            )}
          />
          <Icon className="h-3 w-3 shrink-0 text-ink-muted" />
          <span className="truncate text-2xs text-ink">{table.name}</span>
          {table.denied && (
            <Lock className="h-2.5 w-2.5 shrink-0 text-lock" aria-label="Restricted" />
          )}
        </button>

        {!table.denied && (
          <span className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
            <button
              className="rounded px-1 text-2xs text-ink-faint hover:text-accent"
              onClick={() => onInsert(reference)}
              title={`Insert ${reference} at the cursor`}
            >
              insert
            </button>
            {/*
              Browsing a table is the most common thing anyone does first, and
              it does not deserve a hand-typed query. Opens its own tab so it
              never overwrites whatever is already in the editor.
            */}
            <button
              className="rounded p-0.5 text-ink-faint hover:text-ok"
              onClick={() => onQuickRun(database, table.name)}
              title={`Open a new tab and run SELECT * FROM ${reference}`}
              aria-label={`Preview ${table.name}`}
            >
              <Play className="h-3 w-3 fill-current" />
            </button>
          </span>
        )}

        {table.approxRows !== null && !table.denied && (
          <span className="shrink-0 text-2xs tabular-nums text-ink-faint" title="Engine estimate, not an exact count">
            ~{compact(table.approxRows)}
          </span>
        )}
      </div>

      {table.denied && (
        <p className="py-0.5 pl-11 pr-2 text-2xs italic text-ink-faint">restricted by policy</p>
      )}

      {expanded &&
        !table.denied &&
        table.columns.map((column) => (
          <ColumnNode
            key={column.name}
            column={column}
            onInsert={() => onInsert(column.name)}
          />
        ))}
    </div>
  );
}

function ColumnNode({ column, onInsert }: { column: CatalogColumn; onInsert: () => void }) {
  return (
    <button
      className="group flex w-full items-center gap-1 py-0.5 pl-11 pr-2 text-left hover:bg-surface-sunken"
      onClick={onInsert}
      title={column.comment ?? undefined}
    >
      {column.key === 'PRI' ? (
        <KeyRound className="h-2.5 w-2.5 shrink-0 text-warn" aria-label="Primary key" />
      ) : (
        <Columns3 className="h-2.5 w-2.5 shrink-0 text-ink-faint" />
      )}

      <span
        className={clsx(
          'truncate text-2xs',
          column.restriction ? 'text-ink-muted line-through decoration-lock/60' : 'text-ink-muted',
        )}
      >
        {column.name}
      </span>

      {column.restriction && (
        <Lock
          className="h-2.5 w-2.5 shrink-0 text-lock"
          aria-label={column.restriction === 'DENIED' ? 'Cannot be selected' : 'Returned masked'}
        />
      )}

      <span className="ml-auto shrink-0 truncate font-mono text-2xs text-ink-faint">
        {column.dataType}
      </span>
    </button>
  );
}

function TreeSkeleton() {
  return (
    <div className="space-y-1 px-2 py-1">
      {Array.from({ length: 8 }).map((_, index) => (
        <div
          key={index}
          className="h-4 animate-pulse rounded bg-surface-sunken"
          style={{ width: `${55 + ((index * 13) % 40)}%` }}
        />
      ))}
    </div>
  );
}

function compact(value: number): string {
  return new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 }).format(
    value,
  );
}

function timeAgo(iso: string): string {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}
